import { track } from './tracker';
import { getAnalyticsContext } from './currentContext';
import { PageHiddenReason } from './events';

/**
 * How a visit ended.
 *
 * The browser never reports *why* a page went away — only that it did, plus two hints:
 *
 * - `visibilitychange → hidden` fires in every case that matters (tab switch, app switch, screen
 *   lock, and on mobile right before the tab is destroyed). It is the reliable last chance to send.
 * - `pagehide` with `persisted === false` means the page is genuinely being torn down (cross, tab
 *   swipe, reload); `persisted === true` means it went into bfcache and the client will likely be
 *   back.
 *
 * `beforeunload` is deliberately not used for telemetry: it does not fire on iOS Safari and it
 * suppresses bfcache. The existing `beforeunload` handler in the templates only raises the native
 * confirm dialog and is unrelated.
 *
 * The signal does NOT go through the tracker's transport. Sentry batches spans and loses them when
 * the page is destroyed, and the `/__b/` helper is a `new Image()` whose request is cancelled with
 * the page. Hence a direct `keepalive` request, with `sendBeacon` as the fallback.
 *
 * GET, not POST, on purpose: the front's nginx serves `/__b/` through `try_files $uri $uri/
 * /index.html`, so a POST to a static file answers 405 — and a 405 on this host is already the
 * established marker of "the request hit the wrong ingress".
 */

const BEACON_PATH = '/__b/leave';

let startedAt = 0;
let hiddenAt = 0;
let awaySent = false;
let copied = false;
let acted = false;
let lastAction = '';

const beacon = (reason: PageHiddenReason, persisted: boolean, elapsedMs: number): void => {
  const merchant = getAnalyticsContext()?.merchant;
  const query = [
    `u=${encodeURIComponent(window.location.pathname)}`,
    `r=${reason}`,
    `p=${persisted ? 1 : 0}`,
    `ms=${elapsedMs}`,
    `copied=${copied ? 1 : 0}`,
    `acted=${acted ? 1 : 0}`,
    merchant ? `m=${encodeURIComponent(merchant)}` : '',
    lastAction ? `last=${encodeURIComponent(lastAction)}` : '',
    `t=${Date.now()}`,
  ]
    .filter(Boolean)
    .join('&');
  const url = `${BEACON_PATH}?${query}`;

  try {
    const request = window.fetch(url, {
      method: 'GET',
      keepalive: true,
      mode: 'no-cors',
    });
    if (request && typeof request.catch === 'function') {
      request.catch(() => undefined);
    }
  } catch {
    try {
      navigator.sendBeacon?.(url);
    } catch {
      // nothing else to try — telemetry must never break the page
    }
  }
};

const onAway = (reason: PageHiddenReason, persisted: boolean): void => {
  if (awaySent) return;
  awaySent = true;
  hiddenAt = Date.now();
  const elapsedMs = startedAt ? hiddenAt - startedAt : 0;

  beacon(reason, persisted, elapsedMs);

  const context = getAnalyticsContext();
  if (!context) return;
  try {
    track({
      name: 'page.hidden',
      payload: { ...context, reason, persisted, elapsedMs, copied, acted },
    });
  } catch {
    // analytics must never break UX
  }
};

const onBack = (): void => {
  if (!awaySent) return;
  awaySent = false;
  const awayMs = hiddenAt ? Date.now() - hiddenAt : 0;
  hiddenAt = 0;

  const context = getAnalyticsContext();
  if (!context) return;
  try {
    track({ name: 'page.returned', payload: { ...context, awayMs } });
  } catch {
    // analytics must never break UX
  }
};

/** The client did something deliberate. Used to separate "left without touching anything". */
export const markVisitAction = (name: string): void => {
  acted = true;
  lastAction = name;
};

/**
 * The client took the requisite. Together with the outcome this is our only evidence of "went to
 * the bank app" — the page has no deeplink to a bank, so there is no click to record.
 */
export const markRequisiteCopied = (): void => {
  copied = true;
  acted = true;
};

/** Attaches the listeners and returns the teardown. */
export const initVisitOutcome = (): (() => void) => {
  startedAt = Date.now();

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') onAway('hidden', false);
    else onBack();
  };
  const onPageHide = (e: Event) => {
    onAway('pagehide', (e as PageTransitionEvent).persisted === true);
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);

  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
  };
};

export const _resetVisitOutcomeForTests = (): void => {
  startedAt = 0;
  hiddenAt = 0;
  awaySent = false;
  copied = false;
  acted = false;
  lastAction = '';
};
